/**
 * Convert the first pipe-delimited markdown table in a string into CSV.
 *
 * Handles:
 * - Leading/trailing whitespace per cell
 * - Standard `|---|---|` separator rows (skipped)
 * - Cells that contain commas (quoted) and cells that contain quotes
 *   (doubled per RFC 4180)
 * - Optional edge pipes (`| a | b |` vs `a | b`)
 *
 * Returns `null` when no table is found.
 */

const TABLE_ROW_RE = /^\s*\|?.+\|.+\|?\s*$/
const SEPARATOR_CELL_RE = /^\s*:?[- ]+:?\s*$/

function parseRow(line: string): string[] {
  // Strip optional outer pipes.
  let trimmed = line.trim()
  if (trimmed.startsWith("|")) trimmed = trimmed.slice(1)
  if (trimmed.endsWith("|")) trimmed = trimmed.slice(0, -1)
  return trimmed.split("|").map((cell) => cell.trim())
}

function isSeparatorRow(cells: string[]): boolean {
  if (cells.length === 0) return false
  return cells.every((cell) => SEPARATOR_CELL_RE.test(cell))
}

function escapeCsvCell(cell: string): string {
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`
  }
  return cell
}

export interface MarkdownTableToCsvResult {
  csv: string
  rowCount: number
  columnCount: number
}

/** Extract the first markdown table from ``content`` and render it as CSV. */
export function markdownTableToCsv(content: string): MarkdownTableToCsvResult | null {
  const lines = content.split("\n")
  const rows: string[][] = []
  let inFence = false
  let headerSeen = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.trim().startsWith("```")) {
      inFence = !inFence
      continue
    }
    if (inFence) continue

    if (!TABLE_ROW_RE.test(line)) {
      // If we've started collecting rows, a non-matching line ends the table.
      if (rows.length > 0) break
      continue
    }

    const cells = parseRow(line)
    if (cells.length < 2) continue

    if (!headerSeen) {
      // First matching row: treat as header and look ahead for a separator row.
      const next = lines[i + 1]
      const nextCells = next ? parseRow(next) : []
      if (nextCells.length > 0 && isSeparatorRow(nextCells)) {
        rows.push(cells)
        headerSeen = true
        i++ // skip separator
        continue
      }
      // No separator row follows — this isn't a real markdown table.
      continue
    }

    if (isSeparatorRow(cells)) continue
    rows.push(cells)
  }

  if (rows.length === 0) return null

  const columnCount = Math.max(...rows.map((r) => r.length))
  const normalized = rows.map((r) => {
    const padded = [...r]
    while (padded.length < columnCount) padded.push("")
    return padded
  })

  const csv = normalized.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n")

  return { csv, rowCount: normalized.length, columnCount }
}

/** Trigger a browser download for the given CSV text. */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
